/**
 * Beschikbaarheid Service
 * Berekent realtime beschikbaarheid per medewerker per dag/tijdslot.
 * Basis voor: Outlook werkrooster sync, toekomstig boekingssysteem.
 */

const DAG_KEYS = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

function getDagKey(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const dow = d.getDay(); // 0=zo
  return DAG_KEYS[dow === 0 ? 6 : dow - 1];
}

function parseTime(str) {
  if (!str) return null;
  const parts = str.split(':').map(Number);
  return parts[0] * 60 + (parts[1] || 0);
}

function formatTime(mins) {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

/**
 * Haal werkblokken op uit werkrooster voor een specifieke dag.
 * Returns array van { start: "HH:MM", eind: "HH:MM", type: "ochtend"|"middag"|"avond" }
 */
export function getWerkBlokken(werkrooster, dateStr) {
  if (!werkrooster) return [];
  const dagKey = getDagKey(dateStr);
  const r = werkrooster[dagKey];
  if (!r) return [];
  const blokken = [];
  if (r.ochtend) {
    const [s, e] = r.ochtend.split('-');
    if (s && e) blokken.push({ start: s.trim(), eind: e.trim(), type: 'ochtend' });
  }
  if (r.middag) {
    const [s, e] = r.middag.split('-');
    if (s && e) blokken.push({ start: s.trim(), eind: e.trim(), type: 'middag' });
  }
  if (r.avond) {
    const [s, e] = r.avond.split('-');
    if (s && e) blokken.push({ start: s.trim(), eind: e.trim(), type: 'avond' });
  }
  return blokken;
}

/**
 * Bereken totaal werkuren voor een dag uit werkrooster.
 */
export function getWerkUren(werkrooster, dateStr) {
  const blokken = getWerkBlokken(werkrooster, dateStr);
  let uren = 0;
  for (const b of blokken) {
    const s = parseTime(b.start);
    const e = parseTime(b.eind);
    if (s !== null && e !== null && e > s) uren += (e - s) / 60;
  }
  return uren;
}

/**
 * Haal eerste start en laatste eind tijd op voor een werkdag.
 * Returns { startTijd: "HH:MM", eindTijd: "HH:MM" } of null
 */
export function getWerkTijden(werkrooster, dateStr) {
  const blokken = getWerkBlokken(werkrooster, dateStr);
  if (blokken.length === 0) return null;
  const starts = blokken.map(b => parseTime(b.start)).filter(x => x !== null);
  const einds = blokken.map(b => parseTime(b.eind)).filter(x => x !== null);
  if (starts.length === 0 || einds.length === 0) return null;
  return {
    startTijd: formatTime(Math.min(...starts)),
    eindTijd: formatTime(Math.max(...einds)),
  };
}

/**
 * Bereken beschikbaarheid voor één medewerker op één dag.
 * 
 * @returns {Object} {
 *   werkt: boolean,          // Heeft werkrooster op deze dag
 *   werkBlokken: [],         // Werkrooster blokken
 *   werkTijden: {},          // { startTijd, eindTijd } of null
 *   werkUren: number,        // Totaal werkuren
 *   isFeestdag: boolean,
 *   isVerlof: boolean,       // Heeft goedgekeurd/ingediend verlof
 *   verlofItems: [],         // Verlofaanvragen op deze dag
 *   isZiek: boolean,
 *   isFlexADV: boolean,
 *   roosterWijziging: null|Object,
 *   isVervangen: boolean,    // Dag is geruild (valt weg)
 *   beschikbaar: boolean,    // Kan ingepland worden
 *   beschikbareUren: number, // Beschikbare uren na verlof
 *   status: string,          // 'beschikbaar' | 'verlof' | 'ziek' | 'vrij' | 'feestdag' | 'flexadv'
 *   statusLabel: string,     // Leesbare status
 * }
 */
export function getBeschikbaarheidDag(medewerker, dateStr, verlofaanvragen, roosterWijzigingen, feestdagen) {
  const today = new Date().toISOString().split('T')[0];
  const werkBlokken = getWerkBlokken(medewerker.Werkrooster, dateStr);
  const werkTijden = getWerkTijden(medewerker.Werkrooster, dateStr);
  const werkUren = getWerkUren(medewerker.Werkrooster, dateStr);

  const isFeestdag = feestdagen.some(f => (f.datum || f.Datum) === dateStr);

  // Roosterwijzigingen
  const extraWerk = roosterWijzigingen.find(rw => rw.medewerkerId === medewerker.Id && rw.datum === dateStr);
  const isVervangen = roosterWijzigingen.some(rw => rw.medewerkerId === medewerker.Id && rw.vervangDatum === dateStr);

  // Effectief: werkt deze medewerker op deze dag?
  let werkt = werkBlokken.length > 0 && !isFeestdag && !isVervangen;
  if (extraWerk && !isFeestdag) werkt = true; // Extra werkdag door roosterwijziging

  // Verlof op deze dag
  const verlofItems = verlofaanvragen.filter(v => {
    if (v.medewerkerId !== medewerker.Id) return false;
    if (v.Status !== 'goedgekeurd' && v.Status !== 'ingediend') return false;
    if (dateStr < v.StartDatum) return false;
    if (v.Type === 'ziek' || v.isOpenZiek) {
      if (v.isOpenZiek) return dateStr <= today;
      return dateStr <= v.EindDatum;
    }
    return dateStr <= v.EindDatum;
  });

  const isZiek = verlofItems.some(v => v.Type === 'ziek' || v.isOpenZiek);
  const isFlexADV = verlofItems.some(v => v.Type === 'flexADV' || v.Type === 'adv');
  const isVerlof = verlofItems.length > 0;
  const verlofUren = verlofItems.reduce((sum, v) => {
    // Bij dagdeel: gebruik expliciete uren als beschikbaar
    if (v.Uren && verlofItems.length === 1) return sum + v.Uren;
    return sum + werkUren; // Hele dag
  }, 0);

  const beschikbareUren = Math.max(0, werkUren - (isVerlof ? Math.min(verlofUren, werkUren) : 0));
  const beschikbaar = werkt && !isVerlof && !isFeestdag;

  let status = 'vrij';
  let statusLabel = 'Vrij';
  if (isFeestdag) { status = 'feestdag'; statusLabel = 'Feestdag'; }
  else if (isVervangen) { status = 'vrij'; statusLabel = 'Geruild (vrij)'; }
  else if (isZiek) { status = 'ziek'; statusLabel = 'Ziek'; }
  else if (isFlexADV) { status = 'flexadv'; statusLabel = 'Flex/ADV'; }
  else if (isVerlof) { status = 'verlof'; statusLabel = verlofItems.map(v => v.Type).join(', '); }
  else if (werkt) { status = 'beschikbaar'; statusLabel = `Beschikbaar (${werkUren}u)`; }

  return {
    medewerker, dateStr,
    werkt, werkBlokken, werkTijden, werkUren,
    isFeestdag, isVerlof, verlofItems, isZiek, isFlexADV,
    roosterWijziging: extraWerk || null,
    isVervangen,
    beschikbaar, beschikbareUren,
    status, statusLabel,
  };
}

/**
 * Bereken beschikbaarheid voor ALLE actieve medewerkers op één dag.
 * Handig voor boekingssysteem: "wie is er beschikbaar op datum X?"
 */
export function getBeschikbaarheidAllemaal(medewerkers, dateStr, verlofaanvragen, roosterWijzigingen, feestdagen) {
  const actief = medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht');
  return actief.map(m => getBeschikbaarheidDag(m, dateStr, verlofaanvragen, roosterWijzigingen, feestdagen));
}

/**
 * Check of er voldoende capaciteit is op een bepaald moment.
 * Voor toekomstig boekingssysteem: kan een klant op dit moment een afspraak maken?
 * 
 * @param {string} dateStr - Datum
 * @param {number} benodigdeMedewerkers - Minimaal aantal benodigde medewerkers
 * @param {Array} showroomMedewerkerIds - IDs van medewerkers die showroomdienst kunnen doen
 * @returns {{ beschikbaar: boolean, aantalBeschikbaar: number, medewerkers: Array }}
 */
export function checkCapaciteit(medewerkers, dateStr, verlofaanvragen, roosterWijzigingen, feestdagen, benodigdeMedewerkers = 1, showroomMedewerkerIds = null) {
  const alle = getBeschikbaarheidAllemaal(medewerkers, dateStr, verlofaanvragen, roosterWijzigingen, feestdagen);
  
  // Filter op showroom-medewerkers als opgegeven
  const relevant = showroomMedewerkerIds
    ? alle.filter(b => showroomMedewerkerIds.includes(b.medewerker.Id))
    : alle;
  
  const beschikbareMedewerkers = relevant.filter(b => b.beschikbaar);
  
  return {
    beschikbaar: beschikbareMedewerkers.length >= benodigdeMedewerkers,
    aantalBeschikbaar: beschikbareMedewerkers.length,
    totaalMedewerkers: relevant.length,
    medewerkers: beschikbareMedewerkers.map(b => ({
      id: b.medewerker.Id,
      naam: b.medewerker.Naam,
      werkTijden: b.werkTijden,
      werkUren: b.werkUren,
    })),
    afwezig: relevant.filter(b => !b.beschikbaar).map(b => ({
      id: b.medewerker.Id,
      naam: b.medewerker.Naam,
      reden: b.statusLabel,
    })),
  };
}

/**
 * Genereer een datumreeks.
 */
export function getDateRange(startStr, endStr) {
  const dates = [];
  const current = new Date(startStr + 'T12:00:00');
  const end = new Date(endStr + 'T12:00:00');
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}
